import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { telegramService } from '../../services/telegram.service'

const credentialsSchema = z.object({
  api_id: z.string().min(1, 'API ID is required').transform(Number),
  api_hash: z.string().min(1, 'API Hash is required'),
  phone_number: z.string().min(10, 'Phone number is required'),
})

const codeSchema = z.object({
  code: z.string().min(5, 'Enter the verification code'),
  password: z.string().optional(),
})

type CredentialsFormData = z.infer<typeof credentialsSchema>
type CodeFormData = z.infer<typeof codeSchema>

type Step = 'credentials' | 'code' | '2fa' | 'connected'

export default function TelegramSetup() {
  const queryClient = useQueryClient()
  const [step, setStep] = useState<Step>('credentials')
  const [error, setError] = useState<string | null>(null)

  // Check current status
  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['telegram-status'],
    queryFn: telegramService.getStatus,
  })

  // Credentials form
  const credentialsForm = useForm<CredentialsFormData>({
    resolver: zodResolver(credentialsSchema),
  })

  // Code form
  const codeForm = useForm<CodeFormData>({
    resolver: zodResolver(codeSchema),
  })

  // Send code mutation
  const sendCodeMutation = useMutation({
    mutationFn: telegramService.sendCode,
    onSuccess: (data) => {
      setError(null)
      if (data.status === 'connected') {
        setStep('connected')
        queryClient.invalidateQueries({ queryKey: ['telegram-status'] })
      } else {
        setStep('code')
      }
    },
    onError: (err: { response?: { data?: { detail?: string } } }) => {
      setError(err.response?.data?.detail || 'Failed to send code')
    },
  })

  // Verify code mutation
  const verifyCodeMutation = useMutation({
    mutationFn: telegramService.verifyCode,
    onSuccess: (data) => {
      setError(null)
      if (data.status === '2fa_required') {
        setStep('2fa')
      } else {
        setStep('connected')
        queryClient.invalidateQueries({ queryKey: ['telegram-status'] })
      }
    },
    onError: (err: { response?: { data?: { detail?: string } } }) => {
      setError(err.response?.data?.detail || 'Failed to verify code')
    },
  })

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: telegramService.disconnect,
    onSuccess: () => {
      setStep('credentials')
      queryClient.invalidateQueries({ queryKey: ['telegram-status'] })
    },
  })

  const onSubmitCredentials = (data: CredentialsFormData) => {
    setError(null)
    sendCodeMutation.mutate({
      api_id: data.api_id,
      api_hash: data.api_hash,
      phone_number: data.phone_number,
    })
  }

  const onSubmitCode = (data: CodeFormData) => {
    setError(null)
    verifyCodeMutation.mutate(data)
  }

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (status?.connected || step === 'connected') {
    return (
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Telegram Connected</h3>
              <p className="text-sm text-gray-600">
                @{status?.username || 'Unknown'}
              </p>
            </div>
          </div>
          <button
            onClick={() => disconnectMutation.mutate()}
            disabled={disconnectMutation.isPending}
            className="btn-secondary"
          >
            {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="card max-w-md mx-auto">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Connect Telegram</h2>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {step === 'credentials' && (
        <form onSubmit={credentialsForm.handleSubmit(onSubmitCredentials)} className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              To connect your Telegram account, you need API credentials from{' '}
              <a
                href="https://my.telegram.org/apps"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium"
              >
                my.telegram.org
              </a>
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">API ID</label>
            <input
              {...credentialsForm.register('api_id')}
              type="text"
              placeholder="12345678"
              className="input-field mt-1"
            />
            {credentialsForm.formState.errors.api_id && (
              <p className="mt-1 text-sm text-red-600">
                {credentialsForm.formState.errors.api_id.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">API Hash</label>
            <input
              {...credentialsForm.register('api_hash')}
              type="text"
              placeholder="abc123..."
              className="input-field mt-1"
            />
            {credentialsForm.formState.errors.api_hash && (
              <p className="mt-1 text-sm text-red-600">
                {credentialsForm.formState.errors.api_hash.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Phone Number</label>
            <input
              {...credentialsForm.register('phone_number')}
              type="tel"
              placeholder="+7 999 123 4567"
              className="input-field mt-1"
            />
            {credentialsForm.formState.errors.phone_number && (
              <p className="mt-1 text-sm text-red-600">
                {credentialsForm.formState.errors.phone_number.message}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={sendCodeMutation.isPending}
            className="btn-primary w-full"
          >
            {sendCodeMutation.isPending ? 'Sending code...' : 'Send Verification Code'}
          </button>
        </form>
      )}

      {(step === 'code' || step === '2fa') && (
        <form onSubmit={codeForm.handleSubmit(onSubmitCode)} className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-green-800">
              Check your Telegram app for the verification code.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Verification Code</label>
            <input
              {...codeForm.register('code')}
              type="text"
              placeholder="12345"
              className="input-field mt-1"
              autoFocus
            />
            {codeForm.formState.errors.code && (
              <p className="mt-1 text-sm text-red-600">
                {codeForm.formState.errors.code.message}
              </p>
            )}
          </div>

          {step === '2fa' && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Two-Factor Authentication Password
              </label>
              <input
                {...codeForm.register('password')}
                type="password"
                placeholder="Your 2FA password"
                className="input-field mt-1"
              />
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setStep('credentials')}
              className="btn-secondary flex-1"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={verifyCodeMutation.isPending}
              className="btn-primary flex-1"
            >
              {verifyCodeMutation.isPending ? 'Verifying...' : 'Verify'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
